import { Component, Event, EventEmitter, h, Method, Prop, State, Watch } from "@stencil/core"
import { resolveConsumerInfoFormConfig, validateConsumerInfo } from "../../helpers/ConsumerInfoValidation"
import { ConsumerPhoneNumberValue } from "../phoneNumberInput/ConsumerPhoneNumberValue.model"
import { ConsumerInfoFormConfig } from "./ConsumerInfoFormConfig.model"
import { ConsumerInfoFormField, ConsumerInfoFormValidity } from "./ConsumerInfoFormValidity.model"
import { ConsumerInfoValue, createEmptyConsumerInfoValue } from "./ConsumerInfoValue.model"
import LanguageHandler from "@payair/e-com-common-sdk/core/LanguageHandler"
import { getLanguageHandler, getSupportedLocaleData } from "../../languageHandler/LanguageHandler"
import { scrollElementIntoView } from "../../helpers/ScrollIntoView"
import { getConsumerInfoValidationMessage } from "../../helpers/ConsumerInfoValidationMessage"

type FocusableConsumerField = HTMLElement & { focusInput?: () => Promise<void> }

/** Reusable network-neutral controlled consumer information form. */
@Component({
  tag: "consumer-info-form",
  styleUrl: "ConsumerInfoForm.scss",
  shadow: true,
})
export class ConsumerInfoForm {
  /**
   * Merchant site preferred locale. Format based on ISO format for language (ISO 639-1) and country code
   * (ISO 3166-1 alpha-2). The language and country should be separated using an underscore ( _ ).
   * <br /><br />
   * **Supported locale**: en_GB, sv_SE, pl_PL
   * <br /><br />
   * @default en_GB
   */
  @Prop() locale?: string

  /** Current controlled value. Missing fields are rendered as empty inputs. */
  @Prop() consumerInfoValue: ConsumerInfoValue = createEmptyConsumerInfoValue()

  /** Network-neutral field behaviour and validation constraints. */
  @Prop() config?: ConsumerInfoFormConfig

  /** Network-neutral field errors supplied by the flow owner after outbound mapping validation. */
  @Prop() externalErrors: ConsumerInfoFormValidity["errors"] = []

  /** Whether all fields are disabled. */
  @Prop() disabled: boolean = false

  /** Whether validation errors are displayed before a field loses focus or reportValidity is called. */
  @Prop() showValidationErrors: boolean = false

  /** Emitted with the complete next value whenever the user edits a field. */
  @Event() consumerInfoChange: EventEmitter<ConsumerInfoValue>

  /** Emitted when the validity of the accepted controlled value or configuration changes. */
  @Event() consumerInfoValidityChange: EventEmitter<ConsumerInfoFormValidity>

  /** Emitted when Enter is pressed in a Consumer text field. */
  @Event() consumerInfoSubmit: EventEmitter<void>

  @State() touchedFields: ConsumerInfoFormField[] = []
  @State() reportedValidation: boolean = false
  @State() externalErrorsApplicable: boolean = true

  private lastValiditySignature?: string
  private languageHandler: LanguageHandler
  private fieldElements: Partial<Record<ConsumerInfoFormField, FocusableConsumerField>> = {}

  componentWillLoad() {
    this.languageHandler = getLanguageHandler(getSupportedLocaleData(this.locale).lang)
  }

  componentDidLoad() {
    this.emitValidityIfChanged()
  }

  @Watch("consumerInfoValue")
  @Watch("config")
  emitValidityIfChanged() {
    const validity = this.getCurrentValidity()
    const signature = JSON.stringify(validity)
    if (signature !== this.lastValiditySignature) {
      this.lastValiditySignature = signature
      this.consumerInfoValidityChange.emit(validity)
    }
  }

  @Watch("externalErrors")
  externalErrorsChanged() {
    this.externalErrorsApplicable = true
    this.emitValidityIfChanged()
  }

  /** Returns whether the currently accepted controlled value is valid. */
  @Method()
  async checkValidity(): Promise<boolean> {
    return this.getCurrentValidity().valid
  }

  /** Displays current validation errors and returns whether the accepted controlled value is valid. */
  @Method()
  async reportValidity(): Promise<boolean> {
    this.reportedValidation = true
    return this.getCurrentValidity().valid
  }

  /** Invalidates stale external errors, displays common errors and returns common validity for a new attempt. */
  @Method()
  async reportCommonValidity(): Promise<boolean> {
    this.externalErrorsApplicable = false
    this.reportedValidation = true
    this.emitValidityIfChanged()
    return this.getCurrentValidity().valid
  }

  /** Focuses and scrolls the first invalid field into view in form order. */
  @Method()
  async scrollToFirstError(): Promise<boolean> {
    const firstError = this.getCurrentValidity().errors[0]
    if (!firstError) {
      return false
    }

    const field = this.fieldElements[firstError.field]
    await field?.focusInput?.()
    return scrollElementIntoView(field)
  }

  private getCurrentValue(): ConsumerInfoValue {
    const value = this.consumerInfoValue || createEmptyConsumerInfoValue()
    return {
      firstName: value.firstName || "",
      lastName: value.lastName || "",
      emailAddress: value.emailAddress || "",
      mobileNumber: {
        countryCode: value.mobileNumber?.countryCode || "",
        phoneNumber: value.mobileNumber?.phoneNumber || "",
      },
      countryCode: value.countryCode || undefined,
    }
  }

  private getCurrentValidity(): ConsumerInfoFormValidity {
    return validateConsumerInfo(
      this.getCurrentValue(),
      this.config,
      this.externalErrorsApplicable ? this.externalErrors : []
    )
  }

  private markTouched(field: ConsumerInfoFormField) {
    if (this.touchedFields.indexOf(field) < 0) {
      this.touchedFields = [...this.touchedFields, field]
    }
  }

  private onAtomicFieldBlur = (field: ConsumerInfoFormField, event: CustomEvent<void>) => {
    event.stopPropagation()
    this.markTouched(field)
  }

  private emitValue(value: Partial<ConsumerInfoValue>) {
    const currentValue = this.getCurrentValue()
    this.consumerInfoChange.emit({
      ...currentValue,
      ...value,
      mobileNumber: value.mobileNumber ? { ...value.mobileNumber } : { ...currentValue.mobileNumber },
    })
  }

  private onTextChange = (field: "firstName" | "lastName", event: CustomEvent<string>) => {
    event.stopPropagation()
    this.emitValue({ [field]: event.detail })
  }

  private onEmailChange = (event: CustomEvent<string>) => {
    event.stopPropagation()
    this.emitValue({ emailAddress: event.detail })
  }

  private onMobileNumberChange = (event: CustomEvent<ConsumerPhoneNumberValue>) => {
    event.stopPropagation()
    this.emitValue({ mobileNumber: event.detail })
  }

  private onCountryChange = (event: CustomEvent<string>) => {
    event.stopPropagation()
    this.emitValue({ countryCode: event.detail })
  }

  private onFieldSubmit = (event: CustomEvent<void>) => {
    event.stopPropagation()
    if (this.disabled) {
      return
    }
    this.consumerInfoSubmit.emit()
  }

  private getError(field: ConsumerInfoFormField) {
    return this.getCurrentValidity().errors.find(error => error.field === field)
  }

  private shouldShowError(field: ConsumerInfoFormField): boolean {
    return this.showValidationErrors || this.reportedValidation || this.touchedFields.indexOf(field) >= 0
  }

  private getVisibleError(field: ConsumerInfoFormField) {
    const error = this.getError(field)
    if (!error || !this.shouldShowError(field)) {
      return undefined
    }

    return error
  }

  render() {
    const value = this.getCurrentValue()
    const config = resolveConsumerInfoFormConfig(this.config)
    const firstNameError = this.getVisibleError("firstName")
    const lastNameError = this.getVisibleError("lastName")
    const emailError = this.getVisibleError("emailAddress")
    const mobileNumberError = this.getVisibleError("mobileNumber")
    const countryError = this.getVisibleError("countryCode")

    const firstNameField = (
      <text-input
        ref={element => (this.fieldElements.firstName = element)}
        locale={this.locale}
        labelKey="firstName"
        name="firstName"
        autocomplete="given-name"
        value={value.firstName}
        required={config.firstName.required}
        disabled={this.disabled || config.firstName.disabled}
        invalid={Boolean(firstNameError)}
        errorMessage={firstNameError ? getConsumerInfoValidationMessage(this.languageHandler, firstNameError) : undefined}
        onValueChange={event => this.onTextChange("firstName", event)}
        onFieldBlur={event => this.onAtomicFieldBlur("firstName", event)}
        onFieldSubmit={this.onFieldSubmit}
      />
    )
    const lastNameField = (
      <text-input
        ref={element => (this.fieldElements.lastName = element)}
        locale={this.locale}
        labelKey="lastName"
        name="lastName"
        autocomplete="family-name"
        value={value.lastName}
        required={config.lastName.required}
        disabled={this.disabled || config.lastName.disabled}
        invalid={Boolean(lastNameError)}
        errorMessage={lastNameError ? getConsumerInfoValidationMessage(this.languageHandler, lastNameError) : undefined}
        onValueChange={event => this.onTextChange("lastName", event)}
        onFieldBlur={event => this.onAtomicFieldBlur("lastName", event)}
        onFieldSubmit={this.onFieldSubmit}
      />
    )
    const emailField = (
      <email-input
        ref={element => (this.fieldElements.emailAddress = element)}
        locale={this.locale}
        labelKey="emailAddress"
        value={value.emailAddress}
        autocomplete="email"
        required={config.emailAddress.required}
        disabled={this.disabled || config.emailAddress.disabled}
        showValidationError={false}
        invalid={Boolean(emailError)}
        errorMessage={emailError ? getConsumerInfoValidationMessage(this.languageHandler, emailError) : undefined}
        onValueChange={this.onEmailChange}
        onFieldBlur={event => this.onAtomicFieldBlur("emailAddress", event)}
        onFieldSubmit={this.onFieldSubmit}
      />
    )
    const phoneNumberField = (
      <phone-number-input
        ref={element => (this.fieldElements.mobileNumber = element)}
        locale={this.locale}
        value={value.mobileNumber}
        required={config.mobileNumber.required}
        disabled={this.disabled || config.mobileNumber.disabled}
        showValidationError={false}
        invalid={Boolean(mobileNumberError)}
        errorMessage={
          mobileNumberError ? getConsumerInfoValidationMessage(this.languageHandler, mobileNumberError) : undefined
        }
        onValueChange={this.onMobileNumberChange}
        onFieldBlur={event => this.onAtomicFieldBlur("mobileNumber", event)}
        onFieldSubmit={this.onFieldSubmit}
      />
    )
    const countryField = (
      <select-country
        ref={element => (this.fieldElements.countryCode = element)}
        locale={this.locale}
        value={value.countryCode || ""}
        allowedCountries={config.countryCode.allowedCountries}
        required={config.countryCode.required}
        disabled={this.disabled || config.countryCode.disabled}
        invalid={Boolean(countryError)}
        errorMessage={countryError ? getConsumerInfoValidationMessage(this.languageHandler, countryError) : undefined}
        placeholderKey="enterOrSelect"
        onValueChange={this.onCountryChange}
        onFieldBlur={event => this.onAtomicFieldBlur("countryCode", event)}
      />
    )

    return (
      <div class="consumerInfoForm">
        {emailField}
        <div class="nameFields">
          {firstNameField}
          {lastNameField}
        </div>
        <div class="contactFields">
          {countryField}
          {phoneNumberField}
        </div>
      </div>
    )
  }
}
